import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Upload, Button, Steps, Form, Input, Select, Checkbox, Radio,
  Row, Col, Divider, message, Spin, Alert, Tag, Space, Descriptions,
} from 'antd';
import {
  UploadOutlined, FilePdfOutlined, CheckCircleOutlined,
  LoadingOutlined, EditOutlined, SaveOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload';
import { apiClient } from '../../api/axios';
import { CNPValidator } from '../../utils/cnpValidator';
import { patientApi, CreatePatientDto } from '../../api/patientApi';
import { useAuthStore } from '../../store/authStore';
import { features } from '../../config/features';

const { Step } = Steps;

// Mapping camelCase (Gemini) → snake_case (wizard anamneză)
const CAMEL_TO_SNAKE: Record<string, string> = {
  rightAbdominalPain: 'right_abdominal_pain',
  weightLoss: 'weight_loss',
  lowAppetite: 'low_appetite',
  chronicFatigue: 'chronic_fatigue',
  nauseaVomiting: 'nausea_vomiting',
  bleedingBruising: 'bleeding_bruising',
  hepatitisB: 'hepatitis_b',
  hepatitisC: 'hepatitis_c',
  geneticLiverDisease: 'genetic_liver_disease',
  oncologicOrHormonalTreatments: 'oncologic_or_hormonal_treatments',
  liverCancer: 'liver_cancer',
  // Identice în ambele formate:
  jaundice: 'jaundice', ascites: 'ascites', pruritus: 'pruritus',
  cirrhosis: 'cirrhosis', nafld: 'nafld', diabetes: 'diabetes',
  hypertension: 'hypertension', hepatitis: 'hepatitis',
};

function toSnake(arr: string[]): string[] {
  return (arr || []).map((s) => CAMEL_TO_SNAKE[s] || s);
}

// "yes"/"no" → boolean (pt. câmpuri YesNoCheckbox)
function toBool(v: any): boolean | undefined {
  if (v === 'yes' || v === true) return true;
  if (v === 'no' || v === false) return false;
  return undefined;
}

interface ExtractedPatient {
  cnp: string | null;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  sex: 'M' | 'F' | null;
}

interface ExtractedFormData {
  [key: string]: any;
}

interface ExtractResult {
  patient: ExtractedPatient;
  hepatoForm?: ExtractedFormData;
  dermatoForm?: ExtractedFormData;
  confidence: 'high' | 'medium' | 'low';
  warnings: string[];
}

type ProgramType = 'HEPATO' | 'DERMATO';

const ImportPage = () => {
  const navigate = useNavigate();
  const roleName = useAuthStore((state) => state.user?.role?.name);
  const [currentStep, setCurrentStep] = useState(0);
  const [programType, setProgramType] = useState<ProgramType>(
    roleName?.includes('HEPATO') ? 'HEPATO' : 'DERMATO'
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState<ExtractResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [patientForm] = Form.useForm();
  const [consentPersonal, setConsentPersonal] = useState(false);
  const [consentMedical, setConsentMedical] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);

  /**
   * Faza 2: cu feature flag-ul fluxului clinic inactiv, wizard-ul nu mai este
   * rută înregistrată — importul se finalizează pe fișa pacientului.
   */
  const openPatientWorkspace = (patientId: string, programType: string) => {
    if (!features.clinicalWorkflow) {
      navigate(`/patients/${patientId}`);
      return;
    }
    navigate(`/patients/${patientId}/${programType === 'HEPATO' ? 'hepato-wizard' : 'dermato-wizard'}`);
  };

  // Pas 1: Upload
  const handleExtract = async () => {
    if (fileList.length === 0) {
      message.warning('Încărcați cel puțin fișierul chestionar');
      return;
    }

    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('programType', programType);
      for (const f of fileList) {
        if (f.originFileObj) {
          formData.append('files', f.originFileObj);
        }
      }

      const response = await apiClient.post('/import/extract', formData, {
        // Elimină Content-Type-ul implicit (application/json) ca browserul
        // să seteze automat multipart/form-data cu boundary
        headers: { 'Content-Type': undefined },
        timeout: 180000,
      });

      // Gateway-ul împachetează în { success: true, data: ... }
      const result: ExtractResult = response.data?.data || response.data;
      setExtractResult(result);

      // Pre-populează formularul
      patientForm.setFieldsValue({
        cnp: result.patient.cnp || '',
        firstName: result.patient.firstName || '',
        lastName: result.patient.lastName || '',
        programType,
      });

      setCurrentStep(1);
      message.success('Date extrase! Verificați și corectați mai jos.');
    } catch (error: any) {
      message.error(error.message || 'Eroare la extragere');
    } finally {
      setExtracting(false);
    }
  };

  // Pas 2: Review + Pas 3: Confirmare
  const handleReviewContinue = async () => {
    try {
      const values = await patientForm.validateFields();
      // Validare CNP
      const cnpResult = CNPValidator.validate(values.cnp);
      if (!cnpResult.valid) {
        message.error(cnpResult.error || 'CNP invalid');
        return;
      }
      if (!consentPersonal || !consentMedical) {
        message.warning('Bifați ambele consimțăminte GDPR');
        return;
      }
      // Salvează valorile pentru pasul de confirmare
      setFormValues(values);
      setCurrentStep(2);
    } catch (err) {
      console.error('Validation errors:', err);
      message.error('Completați câmpurile obligatorii');
    }
  };

  const handleConfirm = async () => {
    if (!formValues) {
      message.error('Datele pacientului lipsesc — reveniți la pasul de verificare');
      return;
    }
    setSaving(true);
    try {
      const values = formValues;

      const createData: CreatePatientDto = {
        cnp: values.cnp,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        email: values.email,
        address: values.address,
        city: values.city,
        county: values.county,
        programType: values.programType || programType,
        personalDataConsent: consentPersonal,
        medicalDataConsent: consentMedical,
        consentUserAgent: navigator.userAgent,
      };

      const patient = await patientApi.create(createData);

      // Salvează datele formularului extras (hepato/dermato)
      // Transformă structura Gemini → structura API (items/conditions)
      try {
        if (patient.programType === 'HEPATO' && extractResult?.hepatoForm) {
          const hf = extractResult.hepatoForm;
          await apiClient.post('/hepato-forms', {
            patientId: patient.id,
            currentSymptoms: {
              items: toSnake(hf.currentSymptoms?.checked || hf.currentSymptoms?.items || []),
            },
            personalHistory: {
              conditions: toSnake(hf.personalHistory?.checked || hf.personalHistory?.conditions || []),
            },
            familyHistory: {
              conditions: toSnake(hf.familyHistory?.checked || hf.familyHistory?.conditions || []),
            },
            riskFactors: {
              ...hf.riskFactors,
              toxicExposurePerformed: toBool(hf.riskFactors?.toxicExposurePerformed),
              highRiskTravelPerformed: toBool(hf.riskFactors?.highRiskTravelPerformed),
            },
            previousExams: {
              liverFunctionTestsPerformed: toBool(hf.previousExams?.liverFunctionTestsPerformed),
              imagingPerformed: toBool(hf.previousExams?.imagingPerformed),
            },
            consent: hf.consent || {},
          });
        } else if (patient.programType === 'DERMATO' && extractResult?.dermatoForm) {
          const df = extractResult.dermatoForm;
          await apiClient.post('/dermato-forms', {
            patientId: patient.id,
            programType: 'DERMATO',
            medicalHistoryDetails: df.medicalHistoryDetails || {},
            familyHistoryDetails: df.familyHistoryDetails || {},
            uvRiskFactors: df.uvRiskFactors || {},
            skinEvaluation: df.skinEvaluation || {},
            lifestyle: df.lifestyle || {},
            consent: df.consent || {},
            skinType: df.skinType || df.uvRiskFactors?.skinType,
            notes: df.notes || '',
          });
        }
      } catch (formErr: any) {
        console.warn('Form save failed (patient created):', formErr);
        message.warning('Pacient creat, dar datele formularului nu s-au salvat complet.');
      }

      message.success('Pacient înrolat cu succes!');
      openPatientWorkspace(patient.id, patient.programType);
    } catch (error: any) {
      console.error('Confirm error:', error);
      if (error.response?.status === 409) {
        message.warning('Pacientul există deja — se deschide fișa existentă.');
        try {
          const existing = await patientApi.findByCnp(formValues.cnp);
          if (existing) {
            openPatientWorkspace(existing.id, existing.programType);
            return;
          }
        } catch { /* fallthrough */ }
      }
      const errMsg = error.response?.data?.message
        ? (Array.isArray(error.response.data.message) ? error.response.data.message.join(', ') : error.response.data.message)
        : error.message || 'Eroare la înrolare';
      message.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const uploadProps = {
    beforeUpload: (file: File) => {
      if (file.size > 50 * 1024 * 1024) {
        message.error('Fișierul depășește 50MB');
        return false;
      }
      return true;
    },
    onChange: (info: any) => {
      setFileList(info.fileList);
    },
    multiple: true,
    accept: '.pdf',
  };

  return (
    <div>
      <h1 style={{ marginBottom: 8 }}>Import Pacient din Documente</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>
        Încărcați PDF-urile (chestionar + bilet de ieșire) — datele vor fi extrase automat
        și vor putea fi verificate înainte de înrolare.
      </p>

      <Alert
        type="info"
        message="Import în lot disponibil"
        description={
          <div>
            Pentru importul mai multor pacienți simultan, folosiți{' '}
            <a onClick={() => navigate('/registry-import')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
              Import în lot
            </a>
            {' '}— încărcați un folder cu documente organizate pe pacienți.
          </div>
        }
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Steps current={currentStep} style={{ marginBottom: 32 }}>
        <Step title="Încărcare PDF-uri" icon={currentStep === 0 ? <LoadingOutlined /> : <FilePdfOutlined />} />
        <Step title="Verificare & Corectare" icon={currentStep === 1 ? <EditOutlined /> : <CheckCircleOutlined />} />
        <Step title="Confirmare Înrolare" icon={<SaveOutlined />} />
      </Steps>

      {/* PAS 1: Upload */}
      {currentStep === 0 && (
        <Card title="Încărcați documentele">
          <Form layout="vertical">
            <Form.Item label="Tip program" required>
              <Radio.Group
                value={programType}
                onChange={(e) => setProgramType(e.target.value)}
                options={[
                  { label: 'Dermato (cancer piele)', value: 'DERMATO' },
                  { label: 'Hepato (cancer ficat)', value: 'HEPATO' },
                ]}
                optionType="button"
              />
            </Form.Item>

            <Form.Item
              label="Fișiere PDF"
              required
              extra="Chestionar (obligatoriu) + Bilet de ieșire (pentru CNP și nume). Acceptă PDF scanate."
            >
              <Upload.Dragger {...uploadProps} fileList={fileList} maxCount={5}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">Click sau trageți fișierele aici</p>
                <p className="ant-upload-hint">
                  Suportă: chestionar.pdf, bilet iesire.pdf (max 5 fișiere, 50MB fiecare)
                </p>
              </Upload.Dragger>
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                size="large"
                onClick={handleExtract}
                loading={extracting}
                disabled={fileList.length === 0}
                icon={<FilePdfOutlined />}
              >
                {extracting ? 'Se extrag datele...' : 'Extrage date din PDF-uri'}
              </Button>
            </Form.Item>
          </Form>

          {extracting && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>
                Se analizează documentele cu AI... (poate dura 10-30 secunde)
              </p>
            </div>
          )}
        </Card>
      )}

      {/* PAS 2: Review */}
      {currentStep === 1 && extractResult && (
        <Card
          title="Verificați și corectați datele extrase"
          extra={
            <Tag color={
              extractResult.confidence === 'high' ? 'green' :
              extractResult.confidence === 'medium' ? 'orange' : 'red'
            }>
              Încredere: {
                extractResult.confidence === 'high' ? 'Ridicată' :
                extractResult.confidence === 'medium' ? 'Medie' : 'Scăzută'
              }
            </Tag>
          }
        >
          {extractResult.warnings.length > 0 && (
            <Alert
              type="warning"
              message="Atenție"
              description={<ul style={{ margin: 0, paddingLeft: 20 }}>{extractResult.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>}
              style={{ marginBottom: 16 }}
              showIcon
            />
          )}

          <Divider orientation="left">Date pacient</Divider>
          <Form
            form={patientForm}
            layout="vertical"
            initialValues={{
              cnp: extractResult.patient.cnp || '',
              firstName: extractResult.patient.firstName || '',
              lastName: extractResult.patient.lastName || '',
              programType,
            }}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label="CNP"
                  name="cnp"
                  rules={[
                    { required: true, message: 'CNP obligatoriu' },
                    { len: 13, message: '13 cifre' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const r = CNPValidator.validate(value);
                        return r.valid ? Promise.resolve() : Promise.reject(r.error);
                      },
                    },
                  ]}
                >
                  <Input maxLength={13} placeholder="1234567890123" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Prenume" name="firstName" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Nume" name="lastName" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label="Telefon" name="phone">
                  <Input placeholder="+40712345678" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Email" name="email">
                  <Input placeholder="email@example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Program" name="programType">
                  <Select
                    options={[
                      { label: 'Dermato', value: 'DERMATO' },
                      { label: 'Hepato', value: 'HEPATO' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Adresă" name="address">
              <Input.TextArea rows={2} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Oraș" name="city"><Input /></Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Județ" name="county"><Input /></Form.Item>
              </Col>
            </Row>
          </Form>

          {/* Date formular extrase */}
          {extractResult.hepatoForm && (
            <>
              <Divider orientation="left">Date chestionar Hepato (extrase)</Divider>
              <Descriptions bordered size="small" column={1}>
                {(extractResult.hepatoForm.currentSymptoms?.checked || extractResult.hepatoForm.currentSymptoms?.items) && (
                  <Descriptions.Item label="II. Simptome actuale">
                    {(extractResult.hepatoForm.currentSymptoms.checked || extractResult.hepatoForm.currentSymptoms.items || []).map((s: string) => (
                      <Tag key={s} color="red">{s}</Tag>
                    ))}
                  </Descriptions.Item>
                )}
                {(extractResult.hepatoForm.personalHistory?.checked || extractResult.hepatoForm.personalHistory?.conditions) && (
                  <Descriptions.Item label="III. Istoric medical personal">
                    {(extractResult.hepatoForm.personalHistory.checked || extractResult.hepatoForm.personalHistory.conditions || []).map((s: string) => (
                      <Tag key={s} color="orange">{s}</Tag>
                    ))}
                  </Descriptions.Item>
                )}
                {(extractResult.hepatoForm.familyHistory?.checked || extractResult.hepatoForm.familyHistory?.conditions) && (
                  <Descriptions.Item label="IV. Istoric medical familial">
                    {(extractResult.hepatoForm.familyHistory.checked || extractResult.hepatoForm.familyHistory.conditions || []).map((s: string) => (
                      <Tag key={s} color="purple">{s}</Tag>
                    ))}
                  </Descriptions.Item>
                )}
                {Object.entries(extractResult.hepatoForm.riskFactors || {}).map(([k, v]) => (
                  <Descriptions.Item key={k} label={`V. ${k}`}>
                    {v === 'yes' || v === 'Da' ? <Tag color="red">DA</Tag>
                      : v === 'no' || v === 'Nu' ? <Tag color="green">NU</Tag>
                      : v === null || v === undefined ? <Tag color="default">—</Tag>
                      : <Tag color="orange">{String(v)}</Tag>}
                  </Descriptions.Item>
                ))}
                {Object.entries(extractResult.hepatoForm.previousExams || {}).map(([k, v]) => (
                  <Descriptions.Item key={k} label={`VI. ${k}`}>
                    {v === 'yes' || v === true ? <Tag color="red">DA</Tag>
                      : v === 'no' || v === false ? <Tag color="green">NU</Tag>
                      : <Tag color="default">—</Tag>}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </>
          )}

          {extractResult.dermatoForm && (
            <>
              <Divider orientation="left">Date chestionar Dermato (extrase)</Divider>

              {extractResult.dermatoForm.skinType && (
                <div style={{ marginBottom: 12 }}>
                  <strong>Tip piele:</strong> <Tag color="blue">{extractResult.dermatoForm.skinType}</Tag>
                </div>
              )}

              {extractResult.dermatoForm.medicalHistoryDetails && (
                <Descriptions bordered size="small" column={1} title="II. Istoric Medical Personal" style={{ marginBottom: 16 }}>
                  {Object.entries(extractResult.dermatoForm.medicalHistoryDetails).map(([k, v]) => (
                    <Descriptions.Item key={k} label={k}>
                      {v === 'yes' || v === true ? <Tag color="red">DA</Tag>
                        : v === 'no' || v === false ? <Tag color="green">NU</Tag>
                        : v === null || v === undefined ? <Tag color="default">—</Tag>
                        : <Tag color="orange">{String(v)}</Tag>}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}

              {extractResult.dermatoForm.familyHistoryDetails && (
                <Descriptions bordered size="small" column={1} title="III. Istoric Medical Familial" style={{ marginBottom: 16 }}>
                  {Object.entries(extractResult.dermatoForm.familyHistoryDetails).map(([k, v]) => (
                    <Descriptions.Item key={k} label={k}>
                      {v === 'yes' || v === true ? <Tag color="red">DA</Tag>
                        : v === 'no' || v === false ? <Tag color="green">NU</Tag>
                        : v === 'unknown' ? <Tag color="default">Nu știu</Tag>
                        : v === null || v === undefined ? <Tag color="default">—</Tag>
                        : <Tag color="orange">{String(v)}</Tag>}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}

              {extractResult.dermatoForm.uvRiskFactors && (
                <Descriptions bordered size="small" column={1} title="IV. Expunere UV și Factori de Risc" style={{ marginBottom: 16 }}>
                  {Object.entries(extractResult.dermatoForm.uvRiskFactors).map(([k, v]) => (
                    <Descriptions.Item key={k} label={k}>
                      {v === 'yes' || v === true ? <Tag color="red">DA</Tag>
                        : v === 'no' || v === false ? <Tag color="green">NU</Tag>
                        : v === null || v === undefined ? <Tag color="default">—</Tag>
                        : <Tag color="orange">{String(v)}</Tag>}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}

              {extractResult.dermatoForm.skinEvaluation && (
                <Descriptions bordered size="small" column={1} title="V. Evaluarea Pielii" style={{ marginBottom: 16 }}>
                  {Object.entries(extractResult.dermatoForm.skinEvaluation).map(([k, v]) => (
                    <Descriptions.Item key={k} label={k}>
                      {Array.isArray(v) ? (
                        v.length > 0 ? v.map((x: string) => <Tag key={x} color="orange">{x}</Tag>) : <Tag color="default">—</Tag>
                      ) : v === 'yes' || v === true ? <Tag color="red">DA</Tag>
                        : v === 'no' || v === false ? <Tag color="green">NU</Tag>
                        : v === null || v === undefined ? <Tag color="default">—</Tag>
                        : <Tag color="orange">{String(v)}</Tag>}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}

              {extractResult.dermatoForm.lifestyle && (
                <Descriptions bordered size="small" column={1} title="VI. Stil de Viață">
                  {Object.entries(extractResult.dermatoForm.lifestyle).map(([k, v]) => (
                    <Descriptions.Item key={k} label={k}>
                      {v === 'yes' || v === true ? <Tag color="red">DA</Tag>
                        : v === 'no' || v === false ? <Tag color="green">NU</Tag>
                        : v === null || v === undefined ? <Tag color="default">—</Tag>
                        : <Tag color="orange">{String(v)}</Tag>}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}
            </>
          )}

          <Divider>Consimțământ GDPR</Divider>
          <Space direction="vertical">
            <Checkbox checked={consentPersonal} onChange={(e) => setConsentPersonal(e.target.checked)}>
              Sunt de acord cu prelucrarea datelor cu caracter personal
            </Checkbox>
            <Checkbox checked={consentMedical} onChange={(e) => setConsentMedical(e.target.checked)}>
              Sunt de acord cu prelucrarea datelor medicale
            </Checkbox>
          </Space>

          <Divider />
          <Space>
            <Button type="primary" size="large" onClick={handleReviewContinue} icon={<CheckCircleOutlined />}>
              Continuă la confirmare
            </Button>
            <Button onClick={() => setCurrentStep(0)}>
              Înapoi la upload
            </Button>
          </Space>
        </Card>
      )}

      {/* PAS 3: Confirmare */}
      {currentStep === 2 && (
        <Card title="Confirmați înrolarea">
          <Alert
            type="info"
            message="Verificați încă o dată datele înainte de înrolare"
            description="După confirmare, pacientul va fi creat și veți fi direcționat către formularul complet."
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="CNP">{formValues?.cnp || '—'}</Descriptions.Item>
            <Descriptions.Item label="Nume complet">
              {formValues?.firstName} {formValues?.lastName}
            </Descriptions.Item>
            <Descriptions.Item label="Program">{formValues?.programType || programType}</Descriptions.Item>
            <Descriptions.Item label="Telefon">{formValues?.phone || '—'}</Descriptions.Item>
            <Descriptions.Item label="Email">{formValues?.email || '—'}</Descriptions.Item>
          </Descriptions>

          <Divider />
          <Space>
            <Button type="primary" size="large" onClick={handleConfirm} loading={saving} icon={<SaveOutlined />}>
              Confirmă și Înrolează
            </Button>
            <Button onClick={() => setCurrentStep(1)}>
              Înapoi la verificare
            </Button>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default ImportPage;
